import { resolveEl } from './resolve-el.js';
import { isPlaceholder as RectsIsPlaceholder } from '@ixfx/geometry/rect';
import { Placeholder as PointsPlaceholder, isPlaceholder as PointsIsPlaceholder, Empty as PointsEmpty } from '@ixfx/geometry/point';
import { PlaceholderPositioned as RectsPlaceholderPositioned } from '@ixfx/geometry/rect';
import { clamp } from '@ixfx/numbers';
export const draggable = (elemOrQuery, listener, options = {}) => {
    const elem = resolveEl(elemOrQuery);
    /**
     * Viewport location at drag start
     */
    let initialPointerPosition = PointsPlaceholder;
    let token;
    const autoTranslate = options.autoTranslate ?? false;
    const quickDrag = options.quickDrag ?? false;
    const fence = options.fence ? resolveEl(options.fence) : undefined;
    const fenceViewport = options.fenceViewport;
    let fenceOffset = RectsPlaceholderPositioned;
    const relativePosition = window.getComputedStyle(elem).position === `relative`;
    // De-select if there's a click elsewhere
    const onParentClick = () => {
        const selected = elem.classList.contains(`drag-sel`);
        if (selected) {
            elem.classList.remove(`drag-sel`);
        }
    };
    // Click to select
    const onElementClick = (event) => {
        const selected = elem.classList.contains(`drag-sel`);
        if (selected) {
            elem.classList.remove(`drag-sel`);
        }
        else {
            elem.classList.add(`drag-sel`);
        }
        event.stopPropagation();
    };
    elem.ownerDocument.addEventListener(`click`, onParentClick);
    elem.addEventListener(`click`, onElementClick);
    // Remove event handlers
    const dragCleanup = () => {
        elem.classList.remove(`drag-progress`);
        elem.ownerDocument.removeEventListener(`pointermove`, onPointerMove);
        elem.ownerDocument.removeEventListener(`pointerup`, onPointerUp);
        elem.ownerDocument.removeEventListener(`pointercancel`, onDragCancel);
    };
    const dispose = () => {
        if (elem.classList.contains(`drag-progress`)) {
            onDragCancel(undefined, `dispose`);
        }
        else {
            dragCleanup();
        }
        elem.ownerDocument.removeEventListener(`click`, onParentClick);
        elem.removeEventListener(`click`, onElementClick);
    };
    const validateOffsetAndPoint = (offset, x, y) => {
        if (!RectsIsPlaceholder(fenceOffset)) {
            offset = {
                x: clamp(offset.x, fenceOffset.x, fenceOffset.width),
                y: clamp(offset.y, fenceOffset.y, fenceOffset.height)
            };
            if (fenceViewport) {
                x = clamp(x, fenceViewport.x, fenceViewport.x + fenceViewport.width);
                y = clamp(y, fenceViewport.y, fenceViewport.y + fenceViewport.height);
            }
        }
        return [offset, { x, y }];
    };
    // Dragging
    let lastMoveOffset = PointsEmpty;
    const onPointerMove = (moveEvent) => {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        const { x, y } = moveEvent;
        let offset = PointsIsPlaceholder(initialPointerPosition)
            ? { x: moveEvent.offsetX, y: moveEvent.offsetY }
            : {
                x: x - initialPointerPosition.x,
                y: y - initialPointerPosition.y,
            };
        const r = validateOffsetAndPoint(offset, x, y);
        offset = r[0];
        const state = {
            delta: offset,
            initial: initialPointerPosition,
            token,
            viewport: r[1]
        };
        if (typeof listener.progress !== `undefined`) {
            const p = listener.progress(state);
            if (p.abort) {
                onDragCancel(undefined, `discontinued`);
                return;
            }
            if (p.viewport) {
                offset = {
                    x: p.viewport.x - initialPointerPosition.x,
                    y: p.viewport.y - initialPointerPosition.y
                };
            }
        }
        lastMoveOffset = offset;
        if (autoTranslate) {
            const offsetX = offset.x;
            const offsetY = offset.y;
            elem.style.translate = `${offsetX}px ${offsetY}px`;
        }
    };
    // Done dragging
    const onPointerUp = (upEvent) => {
        const bounds = elem.getBoundingClientRect();
        dragCleanup();
        const { x, y } = upEvent;
        // let offset = {
        //   x: upEvent.x - initialPointerPosition.x,
        //   y: upEvent.y - initialPointerPosition.y,
        // };
        const r = validateOffsetAndPoint(lastMoveOffset, x, y);
        const state = {
            initial: initialPointerPosition,
            token,
            delta: r[0],
            viewport: r[1]
        };
        if (autoTranslate) {
            elem.style.translate = `none`;
            if (relativePosition) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
                const parent = elem.parentElement?.getBoundingClientRect();
                elem.style.left = `${bounds.x - parent.left}px`;
                elem.style.top = `${bounds.y - parent.top}px`;
            }
            else {
                elem.style.left = `${bounds.x}px`;
                elem.style.top = `${bounds.y}px`;
            }
        }
        if (typeof listener.success !== `undefined`) {
            listener.success(state);
        }
    };
    // Drag is cancelled
    const onDragCancel = (event, reason = `pointercancel`) => {
        dragCleanup();
        let viewport = PointsPlaceholder;
        if (event && `x` in event && `y` in event) {
            viewport = { x: event.x, y: event.y };
        }
        const state = {
            token,
            initial: initialPointerPosition,
            delta: { x: -1, y: -1 },
            viewport
        };
        if (typeof listener.abort !== `undefined`) {
            listener.abort(reason, state);
        }
    };
    elem.addEventListener(`pointerdown`, (event) => {
        const selected = elem.classList.contains(`drag-sel`);
        if (!selected && !quickDrag)
            return;
        const event_ = event;
        initialPointerPosition = { x: event_.x, y: event_.y };
        const s = typeof listener.start === `undefined`
            ? { allow: true, token }
            : listener.start();
        if (!s.allow)
            return;
        token = s.token;
        if (fence) {
            const fenceBounds = fence.getBoundingClientRect();
            fenceOffset = {
                x: fenceBounds.x - initialPointerPosition.x,
                y: fenceBounds.y - initialPointerPosition.y,
                width: fenceBounds.x + fenceBounds.width - initialPointerPosition.x,
                height: fenceBounds.y + fenceBounds.height - initialPointerPosition.y,
            };
        }
        else if (fenceViewport) {
            fenceOffset = {
                x: fenceViewport.x - initialPointerPosition.x,
                y: fenceViewport.y - initialPointerPosition.y,
                width: (fenceViewport.width + fenceViewport.x) - initialPointerPosition.x,
                height: (fenceViewport.height + fenceViewport.y) - initialPointerPosition.y
            };
        }
        elem.classList.add(`drag-progress`);
        elem.ownerDocument.addEventListener(`pointermove`, onPointerMove);
        elem.ownerDocument.addEventListener(`pointerup`, onPointerUp);
        elem.ownerDocument.addEventListener(`pointercancel`, onDragCancel);
    });
    return dispose;
};
